// Typed contact-role judgments for the S-2 (intelligence officer) feed and live map.
// Confidence describes role, never the positional accuracy of the sensor report.
import type { Contact, Event } from '../sim/types';
import { TICKS_PER_MIN } from '../sim/types';
import type { EstimatorContext } from './copContext';
import { confidenceBand, type ContactRole, type Estimate, type Estimator } from './estimator';

export interface ContactEstimate {
  enemyId: number;
  tick: number;
  reportedAt: number;
  requestedProvider: Estimator['name'];
  estimate: Estimate<ContactRole>;
}

export const ROLE_LABEL: Record<ContactRole, string> = {
  screen: 'screen', mainBody: 'main body', reserve: 'reserve', unknown: 'unknown role',
};

export async function estimateContactEvent(
  event: Event, context: EstimatorContext, estimator: Estimator,
): Promise<ContactEstimate | undefined> {
  if (event.side !== 0 || !['contact.new', 'nai.contact'].includes(event.kind)) return;
  const enemyId = typeof event.payload.enemyId === 'number' ? event.payload.enemyId : event.unitId;
  const contact = context.contacts.find((c) => c.enemyId === enemyId);
  if (!contact) return;
  return {
    enemyId: contact.enemyId, tick: context.tick, reportedAt: contact.reportedAt,
    requestedProvider: estimator.name,
    estimate: await estimator.contactRole(context, contact),
  };
}

/** A report's historical estimate stays in the feed; only a fresh, calibrated one decorates the map. */
export function currentContactEstimate(
  record: ContactEstimate | undefined, contact: Contact, tick: number, provider: Estimator['name'],
): ContactEstimate | undefined {
  if (!record || record.enemyId !== contact.enemyId || record.requestedProvider !== provider
    || !record.estimate.calibrated || tick < record.tick || tick - record.tick > 5 * TICKS_PER_MIN
    || contact.reportedAt < record.reportedAt || tick - contact.lastSeen >= 10 * TICKS_PER_MIN) return;
  return record;
}

/** Screen-space role ring: coarse bands avoid presenting calibrated role judgment as distance. */
export function roleRingRadiusPx(confidence: number): number {
  return { low: 22, medium: 17, high: 12 }[confidenceBand(confidence)];
}
